import { parse } from "query-string";
import SessionClient from "./SessionClient";
import { handleEmailNotVerified, handleUnauthorized, isForbiddenStatus, isUnauthorizedStatus } from "./sessionExpiry";

class ApiClient {
  static defaults = {
    host: process.env.REACT_APP_POWUR_API_URL,
    baseUrl: "/json_web",
    urls: {
      jwt: "/jwt",
      sso: "/saml/auth",
    },
  };

  constructor(options) {
    this.options = { ...ApiClient.defaults, ...options };
    this.session = new SessionClient();
  }

  getHeaders(options = {}) {
    const headers = {
      "Content-Type": "application/json; charset=utf-8",
      Accept: options.accept || "*/*",
    };
    if (options.type === "csvUpload") delete headers["Content-Type"];
    const token = this.session.jwt;
    if (token) {
      headers.Authorization = this.authorizationHeader(token);
    }
    return headers;
  }

  authorizationHeader(token) {
    return `Bearer ${token}`;
  }

  url(key) {
    return this.options.urls[key];
  }

  isJson = (response) => {
    const contentType = response.headers.get("content-type");
    return contentType && contentType.includes("application/json");
  };

  status = (options) => (response) => {
    if (response.error) {
      this.handleError(response, options);
      return Promise.reject(response);
    }

    if (response.redirect) {
      window.location.href = response.redirect;
    }

    return Promise.resolve(response);
  };

  normalizeBody = (response) => {
    if (this.isJson(response)) {
      return response.json();
    }

    if (response.status >= 200 && response.status < 300) {
      response.text();
    }

    return {
      error: {
        status: response.status,
        message: response.statusText,
        response,
      },
    };
  };

  handleError(data, options) {
    const isForbidden = isForbiddenStatus(data.error.status);
    if (isForbidden && !options.noEmailVerifiedRedirect) {
      const msg = data.error.message || data.error.response?.error?.message || data.error.response?.message;
      if (msg === "email_not_verified") {
        handleEmailNotVerified();
        return data;
      }
    }
    if (isUnauthorizedStatus(data.error.status)) {
      handleUnauthorized(this.session, options);
    }
    return data;
  }

  invoke(url, params, options = {}) {
    // Build the fetch init from an explicit allowlist so an option fetch doesn't
    // need (or one a caller didn't mean to forward) can't rewrite the request.
    const {
      method = "GET",
      headers,
      body,
      signal: callerSignal,
      timeoutMs = 30000,
      no401redirect,
      noEmailVerifiedRedirect,
    } = options;

    const uri = new URL(`${this.options.host}${this.options.baseUrl}${url}`);

    const reqOptions = {
      method,
      headers: {
        ...this.getHeaders(options),
        ...(headers || {}),
      },
    };
    if (body) reqOptions.body = body;

    const sessionUserId = this.session.user?.props?.id;
    if (this.session.jwt && sessionUserId) {
      uri.searchParams.append("t_id", sessionUserId);
    }

    if (method === "GET") {
      Object.keys(params).forEach((key) => {
        uri.searchParams.append(key, params[key]);
      });
    } else if (!reqOptions.body) {
      reqOptions.body = options.type === "csvUpload" ? params.file : JSON.stringify(params);
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    if (callerSignal) {
      if (callerSignal.aborted) controller.abort();
      else callerSignal.addEventListener("abort", () => controller.abort(), { once: true });
    }
    reqOptions.signal = controller.signal;

    return fetch(uri, reqOptions)
      .then(this.normalizeBody)
      .then(this.status({ no401redirect, noEmailVerifiedRedirect }))
      .finally(() => clearTimeout(timeoutId));
  }

  get(url, params, options) {
    return this.invoke(url, params, options);
  }

  post(url, params, options) {
    return this.invoke(url, params, { ...options, method: "POST" });
  }

  patch(url, params, options) {
    return this.invoke(url, params, { ...options, method: "PATCH" });
  }

  put(url, params, options) {
    return this.invoke(url, params, { ...options, method: "PUT" });
  }

  delete(url, params, options) {
    return this.invoke(url, params, { ...options, method: "DELETE" });
  }

  login(params) {
    const result = this.post(this.url("jwt"), params, { no401redirect: true });
    return result;
  }

  ssoLogin(route = null, relay = null) {
    document.getElementById("sso_jwt").value = this.authorizationHeader(this.session.jwt);

    if (route) {
      document.getElementById("sso_route").value = route;
    }

    if (relay) {
      document.getElementById("sso_relay_state").value = relay;
    }

    return document.getElementById("sso_form").submit();
  }

  isSso = () => {
    return parse(window.location.search).sso;
  };
}

export default ApiClient;
